import { useRef } from 'react';

export const shallowEqual = (a: Record<string, any>, b: Record<string, any>): boolean => {
  if (a === b) return true;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => Object.hasOwn(b, k) && Object.is(a[k], b[k]));
};

/**
 * Keeps a stable reference for a value that is recomputed on every render.
 *
 * Use it instead of `useMemo` when the value must be recomputed every render to
 * stay correct — for example when it derives from inline callbacks that read
 * enclosing scope — but consumers would otherwise see a fresh object each time
 * and re-fire every effect keyed on it.
 *
 * The comparison is one level deep, so callers must replace top-level keys
 * rather than mutating nested values in place.
 */
const useStable = <V extends Record<string, any>>(next: V): V => {
  const ref = useRef(next);
  if (!shallowEqual(ref.current, next)) {
    ref.current = next;
  }
  return ref.current;
};

export default useStable;
